/* Theme-owned Compare tabs. Exact source header/panel order and initial state remain in HTML.
 * The source's extra product-media/slider hooks do not apply: all six panels contain only images. */
(() => {
  'use strict';
  const initialized = new WeakSet();
  function init(scope = document) {
    const roots = [...scope.querySelectorAll('[data-native-compare-page="compare"]')];
    if (scope.matches?.('[data-native-compare-page="compare"]')) roots.unshift(scope);
    roots.forEach(root => root.querySelectorAll('[data-native-kind="Tabs"]').forEach(tabs => {
      if (initialized.has(tabs)) return;
      const menu = tabs.querySelector('[data-native-kind="TabsMenu"]');
      if (!menu || !menu.nextElementSibling) return;
      const headers = [...menu.children];
      const panels = [...menu.nextElementSibling.children];
      if (headers.length !== 6 || panels.length !== 6) throw new Error('Unexpected Compare tab structure');
      initialized.add(tabs);
      menu.addEventListener('click', event => {
        event.stopPropagation();
        const header = event.target.closest('[data-native-kind="TabHeader2"]');
        const index = headers.indexOf(header);
        if (index < 0) return;
        headers.forEach((item, i) => item.setAttribute('data-native-tab-active', String(i === index)));
        panels.forEach((item, i) => { item.style.display = i === index ? 'block' : 'none'; });
      });
    }));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

/* Theme-owned controls for the explicitly audited awards rail. Source ContentList GRID nodes do not initialize controls.
 * Source contracts: runtime-source/carousel-contract.json. No external runtime. */
(() => {
  const states = new WeakMap();
  const device = () => innerWidth <= 767 ? 'mobile' : innerWidth <= 1024 ? 'tablet' : innerWidth <= 1200 ? 'laptop' : 'all';
  function initialize(owner) {
    states.get(owner)?.destroy();
    const config = JSON.parse(owner.dataset.nativeCompareCarousel);
    const track = owner.classList.contains('np-compare-slider') ? owner : owner.querySelector('.np-compare-slider');
    if (!track) throw new Error('Missing native carousel track');
    const slides = [...track.children].filter(node => node.classList.contains('np-compare-slide'));
    if (!slides.length) throw new Error('Missing native carousel slides');
    if (config.maxHeight !== true) throw new Error('Unaudited variable-height carousel');
    const abort = new AbortController(), { signal } = abort;
    const previous = track.querySelector(':scope > .np-compare-previous');
    const next = track.querySelector(':scope > .np-compare-next');
    const pagination = track.querySelector(':scope > .np-compare-pagination');
    let current = 0, viewport, timer, scrollTimer, visibleObserver;
    const show = () => Number(config.slidesToShow[viewport]);
    const step = () => Number(config.slidesToScroll[viewport]);
    const finalIndex = () => Math.max(0, slides.length - show());
    const pageCount = () => Math.max(1, Math.ceil((slides.length - show()) / step()) + 1);
    function update() {
      if (pagination && config.paginationStyle !== 'none') {
        const selected = Math.min(Math.ceil(current / step()), pagination.children.length - 1);
        [...pagination.children].forEach((button, index) => {
          button.classList.toggle('np-compare-active', index === selected);
          button.setAttribute('aria-current', index === selected ? 'true' : 'false');
        });
      }
      if (previous && next && config.navStyle !== 'none' && !config.loop) {
        previous.style.visibility = current <= 0 || slides.length <= show() ? 'hidden' : '';
        next.style.visibility = current >= finalIndex() || slides.length <= show() ? 'hidden' : '';
      }
    }
    function go(index) {
      current = Math.max(0, Math.min(slides.length - 1, index));
      const slide = slides[current];
      track.scroll({ left: Math.min(track.scrollWidth, Math.max(0, slide.offsetLeft)), top: Math.min(track.scrollHeight, Math.max(0, slide.offsetTop)), behavior: 'smooth' });
      update();
    }
    function move(direction) {
      if (direction > 0) {
        if (current >= finalIndex()) { if (config.loop) go(0); }
        else go(Math.min(finalIndex(), current + step()));
      } else if (current === 0) { if (config.loop) go(finalIndex()); }
      else go(Math.max(0, current - step()));
    }
    function startTimer() {
      clearInterval(timer);
      if (config.autoPlay === true) {
        timer = setInterval(() => {
          let target = current + step();
          if (config.loop && (current + 1 === slides.length || current + 1 + show() > slides.length)) target = 0;
          if (slides[target]) go(target);
        }, Number(config.autoPlayDelay));
      }
    }
    function measure() {
      const nextViewport = device();
      if (viewport === nextViewport) return;
      viewport = nextViewport;
      slides.forEach((slide, index) => { slide.style.scrollSnapStop = index % step() === 0 ? 'always' : ''; });
      if (pagination && config.paginationStyle !== 'none') {
        pagination.replaceChildren();
        for (let index = 0; index < pageCount(); index++) {
          const button = document.createElement('button');
          button.type = 'button'; button.setAttribute('aria-label', `Go to slide ${index + 1}`);
          button.addEventListener('click', event => { event.stopPropagation(); go(index * step()); }, { signal });
          pagination.append(button);
        }
      }
      // maxHeight=true retains the source CSS's natural full-rail height.
      update(); startTimer();
    }
    next?.addEventListener('click', event => { event.stopPropagation(); if (!event.detail || event.detail === 1) move(1); }, { signal });
    previous?.addEventListener('click', event => { event.stopPropagation(); if (!event.detail || event.detail === 1) move(-1); }, { signal });
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        current = slides.reduce((best, slide, index) => Math.abs(slide.offsetLeft - track.scrollLeft) < Math.abs(slides[best].offsetLeft - track.scrollLeft) ? index : best, 0);
        update();
      }, 100);
    }, { signal, passive: true });
    if (config.pauseOnHover === true) {
      track.addEventListener('mouseover', () => clearInterval(timer), { signal });
      track.addEventListener('mouseleave', startTimer, { signal });
    }
    if ('IntersectionObserver' in window) {
      visibleObserver = new IntersectionObserver(entries => entries.forEach(entry => entry.target.classList.toggle('np-compare-visible', entry.isIntersecting)), { root: track, threshold: 0.9 });
      slides.forEach(slide => visibleObserver.observe(slide));
    }
    window.addEventListener('resize', measure, { signal });
    states.set(owner, { destroy() { abort.abort(); clearInterval(timer); clearTimeout(scrollTimer); visibleObserver?.disconnect(); } });
    measure();
  }
  function init(root = document) {
    const owners = [...root.querySelectorAll('[data-native-compare-carousel]')];
    if (root.matches?.('[data-native-compare-carousel]')) owners.unshift(root);
    owners.forEach(initialize);
  }
  window.NativeCompareCarousels = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();

/* Native lazy-background lifecycle; source evidence is background-contract.json. */
(function installNativeCompareBackground() {
  'use strict';
  if (window.NativeCompareBackground) return;
  const selector = '.npcmp-57fdb6011cd3';
  const marker = 'npcmp-57fdb6011cd3';
  function elements(root) {
    const list = Array.from(root.querySelectorAll(selector));
    if (typeof root.matches === 'function' && root.matches(selector)) list.unshift(root);
    return list;
  }
  function init(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    window.setTimeout(function initializeObserver() {
      if ('IntersectionObserver' in window) {
        const observer = new window.IntersectionObserver(function reveal(entries) {
          entries.forEach(function onEntry(entry) {
            if (entry.isIntersecting) {
              entry.target.classList.remove(marker);
              observer.unobserve(entry.target);
            }
          });
        }, {rootMargin: '5%'});
        elements(root).forEach(element => observer.observe(element));
      } else {
        window.setTimeout(function noObserverFallback() {
          elements(root).forEach(element => element.classList.remove(marker));
        }, 3000);
      }
    });
    window.setTimeout(function maximumDelayFallback() {
      elements(root).forEach(element => element.classList.remove(marker));
    }, 10000);
  }
  window.NativeCompareBackground = {init};
  document.addEventListener('shopify:section:load', event => init(event.target));
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), {once: true});
  } else {
    init(document);
  }
})();
